import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type RefObject } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiClient, responseData } from "@/api/client";
import type {
  OtaCollectionStatus,
  OtaReservation,
  PageResponse,
  ReconciliationMaster,
  ReservationSyncIssue,
  ReservationSyncRun,
} from "@/api/types";
import { useWorkspace } from "@/app/workspace";
import { DownloadIcon } from "@/components/DomainShell";
import { DomainContext } from "@/components/DomainContext";
import { Empty, PageLead, Panel } from "@/components/Page";
import { Pagination } from "@/components/Pagination";
import { useToast } from "@/components/Toast";
import { money, shortDate } from "@/components/format";

type View = "data" | "syncs";
const OTA_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

type OtaStatusFilter = OtaCollectionStatus | Lowercase<OtaCollectionStatus> | "all";
const otaStatusFilter = (value: string): OtaStatusFilter => {
  switch (value) {
    case "PENDING": case "CLAIMED": case "PREPURCHASE": case "COLLECTED":
    case "pending": case "claimed": case "prepurchase": case "collected": case "all": return value;
    default: return "all";
  }
};
type OtaReservationStatusFilter = "confirmed" | "checked_out" | "cancelled" | "all";
const otaReservationStatusFilter = (value: string): OtaReservationStatusFilter => {
  switch (value.toLowerCase()) {
    case "confirmed": case "checked_out": case "cancelled": case "all": return value.toLowerCase() as OtaReservationStatusFilter;
    default: return "all";
  }
};

const collectionStatusLabel: Record<OtaCollectionStatus, string> = {
  PENDING: "Pendiente",
  CLAIMED: "Reclamada",
  PREPURCHASE: "Precompra",
  COLLECTED: "Cobrada",
};
const collectionStatusTone: Record<OtaCollectionStatus, string> = {
  PENDING: "warning",
  CLAIMED: "danger",
  PREPURCHASE: "info",
  COLLECTED: "success",
};
const reservationStatusLabel: Record<OtaReservation["status"], string> = {
  CONFIRMED: "Confirmada",
  CHECKED_OUT: "Checkout",
  CANCELLED: "Cancelada",
  MIXED: "Estados mixtos",
};
const reservationStatusTone: Record<OtaReservation["status"], string> = {
  CONFIRMED: "info",
  CHECKED_OUT: "info",
  CANCELLED: "danger",
  MIXED: "warning",
};

const statusLabel: Record<string, string> = {
  PENDING: "En cola",
  PROCESSING: "Consultando Reservas",
  CANCELLING: "Cancelando",
  COMPLETED: "Completada",
  FAILED: "Fallida",
  CANCELLED: "Cancelada",
};
const stageLabel: Record<ReservationSyncRun["stage"], string> = {
  QUEUED: "Esperando ejecución",
  PREPARING: "Preparando consulta",
  DISCOVERING: "Buscando reservas",
  FETCHING: "Leyendo reservas",
  PERSISTING: "Procesando reservas",
  FINALIZING: "Finalizando",
};
const activeStatuses: ReservationSyncRun["status"][] = [
  "PENDING",
  "PROCESSING",
  "CANCELLING",
];
const isActiveRun = (run?: ReservationSyncRun | null) =>
  Boolean(run && activeStatuses.includes(run.status));
const statusTone = (status?: string) =>
  status === "COMPLETED"
    ? "success"
    : status === "FAILED"
      ? "danger"
      : status === "PROCESSING" || status === "PENDING"
        ? "info"
        : "warning";
const dateTime = (value?: string) =>
  value
    ? new Intl.DateTimeFormat("es-AR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
const clockTime = (value?: string) =>
  value
    ? new Intl.DateTimeFormat("es-AR", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }).format(new Date(value))
    : "—";
const syncDuration = (run: ReservationSyncRun, now: number) => {
  if (!run.startedAt) return "No iniciada";
  const startedAt = new Date(run.startedAt).valueOf();
  const finishedAt = run.finishedAt
    ? new Date(run.finishedAt).valueOf()
    : isActiveRun(run)
      ? now
      : Number.NaN;
  if (
    !Number.isFinite(startedAt) ||
    !Number.isFinite(finishedAt) ||
    finishedAt < startedAt
  )
    return "—";
  const totalSeconds = Math.floor((finishedAt - startedAt) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0)
    return `${hours} h ${String(minutes).padStart(2, "0")} min ${String(seconds).padStart(2, "0")} s`;
  if (minutes > 0)
    return `${minutes} min ${String(seconds).padStart(2, "0")} s`;
  return `${seconds} s`;
};
const monthEnd = (period: string) => {
  const next = new Date(`${period}-01T00:00:00Z`);
  next.setUTCMonth(next.getUTCMonth() + 1);
  return new Date(next.valueOf() - 86_400_000).toISOString().slice(0, 10);
};
const dateInputValue = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
const lastCompleteMonthRange = () => {
  const now = new Date(Date.now());
  return {
    from: dateInputValue(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
    to: dateInputValue(new Date(now.getFullYear(), now.getMonth(), 0)),
  };
};
const rangeError = (from: string, to: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to))
    return "Informá las fechas desde y hasta.";
  const start = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  if (
    Number.isNaN(start.valueOf()) ||
    Number.isNaN(end.valueOf()) ||
    start.toISOString().slice(0, 10) !== from ||
    end.toISOString().slice(0, 10) !== to
  )
    return "El rango contiene una fecha inválida.";
  if (start > end) return "Desde no puede ser posterior a Hasta.";
  const days = Math.floor((end.valueOf() - start.valueOf()) / 86_400_000) + 1;
  return days > 366 ? "El rango no puede superar los 366 días." : "";
};

function VeniceSyncIcon({ active = false }: { active?: boolean }) {
  return (
    <svg
      className={`venice-sync-icon${active ? " venice-sync-icon--active" : ""}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <g className="venice-sync-icon__orbit">
        <path d="M5.35 9a7.25 7.25 0 0 1 11.88-3.12L19 7.65" />
        <path d="M19 4.35v3.3h-3.3" />
        <path d="M18.65 15a7.25 7.25 0 0 1-11.88 3.12L5 16.35" />
        <path d="M5 19.65v-3.3h3.3" />
      </g>
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 10.8v5" />
      <path d="M12 7.5h.01" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m4 16.5-.8 4.3 4.3-.8L18.8 8.7l-3.5-3.5L4 16.5Z" />
      <path d="m13.8 6.7 3.5 3.5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m6 6 12 12M18 6 6 18" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  );
}

function CollectionStatusEditor({
  row,
  saving,
  onSave,
}: {
  row: OtaReservation;
  saving: boolean;
  onSave(status: OtaCollectionStatus): void;
}) {
  return (
    <>
      <select
        className={`ota-collection-status-input ota-collection-status-input--${collectionStatusTone[row.collectionStatus]}`}
        aria-label={`Estado de cobranza de reserva ${row.reservationId}`}
        value={row.collectionStatus}
        disabled={saving}
        onChange={(event) =>
          onSave(event.target.value as OtaCollectionStatus)
        }
      >
        {Object.entries(collectionStatusLabel).map(([value, label]) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>
    </>
  );
}

function CollectionNotesEditor({
  row,
  saving,
  onSave,
}: {
  row: OtaReservation;
  saving: boolean;
  onSave(notes: string): void;
}) {
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(row.notes);
  const input = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    setNotes(row.notes);
  }, [row.notes]);
  const startEditing = () => {
    setNotes(row.notes);
    setEditing(true);
    requestAnimationFrame(() => input.current?.focus());
  };
  return (
    <div className={`ota-notes-editor${editing ? " is-editing" : ""}`}>
      <div className="ota-notes-editor__field">
        <textarea
          ref={input}
          aria-label={`Observaciones de reserva ${row.reservationId}`}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Sin observaciones"
          readOnly={!editing}
          rows={1}
        />
        {editing ? (
          <div className="ota-notes-editor__actions">
            <button
              type="button"
              className="ota-notes-editor__button ota-notes-editor__cancel"
              aria-label={`Cancelar edición de observaciones de reserva ${row.reservationId}`}
              title="Cancelar cambios"
              disabled={saving}
              onClick={() => {
                setNotes(row.notes);
                setEditing(false);
              }}
            >
              <CloseIcon />
            </button>
            <button
              type="button"
              className="ota-notes-editor__button ota-notes-editor__save"
              aria-label={`Guardar observaciones de reserva ${row.reservationId}`}
              title="Guardar cambios"
              disabled={saving}
              onClick={() => {
                onSave(notes);
                setEditing(false);
              }}
            >
              <CheckIcon />
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="ota-notes-editor__button ota-notes-editor__edit"
            aria-label={`Editar observaciones de reserva ${row.reservationId}`}
            title="Editar observaciones"
            disabled={saving}
            onClick={startEditing}
          >
            <PencilIcon />
          </button>
        )}
      </div>
    </div>
  );
}

function ReservationColumnHeader({
  allVisibleSelected,
  hasVisibleReservations,
  onTogglePage,
  selectPageRef,
  scrollLeft,
  layout,
}: {
  allVisibleSelected: boolean;
  hasVisibleReservations: boolean;
  onTogglePage(): void;
  selectPageRef: RefObject<HTMLInputElement | null>;
  scrollLeft: number;
  layout: { width: number; columns: number[] };
}) {
  const style = layout.columns.length
    ? {
        width: `${layout.width}px`,
        gridTemplateColumns: layout.columns.map((value) => `${value}px`).join(" "),
        transform: `translateX(${-scrollLeft}px)`,
      }
    : { transform: `translateX(${-scrollLeft}px)` };
  return (
    <div className="ota-reservation-column-header-viewport" role="group" aria-label="Columnas de reservas">
      <div className="ota-reservation-column-header" style={style}>
        <div className="ota-selection-cell">
          <label className="check-field">
            <input
              ref={selectPageRef}
              type="checkbox"
              aria-label="Seleccionar esta página"
              checked={allVisibleSelected}
              disabled={!hasVisibleReservations}
              onChange={onTogglePage}
            />
          </label>
        </div>
        <div>Agencia / origen</div>
        <div>Reserva</div>
        <div>Estado</div>
        <div>Huésped</div>
        <div>Estadía</div>
        <div className="ota-amount-column">
          <span className="ota-amount-heading">
            Importe
            <button
              type="button"
              className="ota-amount-info"
              aria-label="Información sobre el importe"
              aria-describedby="ota-amount-net-tooltip"
            >
              <InfoIcon />
              <span
                id="ota-amount-net-tooltip"
                className="ota-amount-info__tooltip"
                role="tooltip"
              >
                Importes netos sin IVA
              </span>
            </button>
          </span>
        </div>
        <div>Cobranza</div>
        <div>Observaciones</div>
      </div>
    </div>
  );
}

function SyncRunIssues({ run }: { run: ReservationSyncRun }) {
  const count = (run.warningCount ?? 0) + (run.errorCount ?? 0);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const trigger = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLElement>(null);
  const titleId = `ota-run-issues-title-${run.id}`;
  const issues = useQuery({
    queryKey: ["ota-sync-issues", run.id, page],
    queryFn: (): Promise<PageResponse<ReservationSyncIssue>> => responseData(apiClient.GET("/api/v1/ota/reservation-syncs/{id}/issues", {
      params: { path: { id: run.id }, query: { page: String(page), pageSize: "25" } },
    })),
    enabled: open && count > 0,
  });

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => closeButton.current?.focus());
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialog.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), select:not(:disabled), a[href]',
        ) ?? [],
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", keydown);
      trigger.current?.focus();
    };
  }, [open]);

  if (count === 0) return null;

  const danger = (run.errorCount ?? 0) > 0;
  const openModal = () => {
    setPage(1);
    setOpen(true);
  };

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={`ota-run-issue-button ota-run-issue-button--${danger ? "danger" : "warning"}`}
        aria-label={`Ver ${count} ${count === 1 ? "incidencia" : "incidencias"} de la sincronización del ${dateTime(run.createdAt)}`}
        onClick={openModal}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3 2.7 20h18.6L12 3Z" />
          <path d="M12 9v5" />
          <circle cx="12" cy="17" r=".8" />
        </svg>
        <span className="ota-run-issue-button__count" aria-hidden="true">
          {count}
        </span>
      </button>
      {open && (
        <div
          className="modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setOpen(false);
          }}
        >
          <section
            ref={dialog}
            className="import-modal ota-issues-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
          >
            <header>
              <div>
                <small>Sincronización · {dateTime(run.createdAt)}</small>
                <h2 id={titleId}>Incidencias de la sincronización</h2>
              </div>
              <button
                ref={closeButton}
                type="button"
                className="modal-close"
                aria-label="Cerrar modal"
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </header>
            <div className="ota-issues-modal__body">
              <dl className="ota-issues-modal__context">
                <div>
                  <dt>Estado</dt>
                  <dd>
                    <span className={`status status--${statusTone(run.status)}`}>
                      {statusLabel[run.status]}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>Rango consultado</dt>
                  <dd className="mono">
                    {run.queryFrom} → {run.queryTo}
                  </dd>
                </div>
              </dl>
              <div className="ota-modal-issue-list" aria-live="polite">
                {issues.isPending && (
                  <p className="ota-modal-issues__state">
                    Cargando incidencias…
                  </p>
                )}
                {issues.isError && (
                  <p className="ota-modal-issues__state ota-modal-issues__state--error" role="alert">
                    No se pudieron cargar las incidencias.
                    {run.errorMessage ? ` ${run.errorMessage}` : ""}
                  </p>
                )}
                {issues.data?.items.map((issue) => (
                  <article
                    className={`ota-modal-issue ota-modal-issue--${issue.severity}`}
                    key={issue.id}
                  >
                    <span
                      className={`status status--${issue.severity === "error" ? "danger" : "warning"}`}
                    >
                      {issue.severity === "error" ? "Error" : "Advertencia"}
                    </span>
                    <div>
                      <strong>{issue.message}</strong>
                      <small>
                        {issue.code}
                        {issue.externalReservationId
                          ? ` · Reserva ${issue.externalReservationId}`
                          : ""}
                      </small>
                    </div>
                  </article>
                ))}
                {issues.data && !issues.data.items.length && (
                  <p className="ota-modal-issues__state">
                    {run.errorMessage ||
                      "Esta ejecución no registró detalles."}
                  </p>
                )}
              </div>
              <Pagination meta={issues.data?.meta} onPageChange={setPage} />
            </div>
          </section>
        </div>
      )}
    </>
  );
}

function SyncTelemetry({
  run,
  cancelling,
  onCancel,
}: {
  run: ReservationSyncRun;
  cancelling: boolean;
  onCancel(): void;
}) {
  const counter =
    run.stageTotal > 0
      ? `${run.stageCompleted} de ${run.stageTotal}`
      : "Calculando total";
  const healthLabel =
    run.status === "CANCELLING"
      ? "Cancelación solicitada"
      : run.health === "STALE"
        ? "Proceso sin respuesta"
        : run.health === "WAITING"
          ? "Esperando worker"
          : "Proceso activo";
  return (
    <div
      className={`ota-run-telemetry ota-run-telemetry--${run.health.toLowerCase()}${run.status === "CANCELLING" ? " ota-run-telemetry--cancelling" : ""}`}
    >
      <div className="ota-run-telemetry__reading">
        <span
          className="ota-run-telemetry__signal"
          aria-hidden="true"
        />
        <span className="ota-run-telemetry__copy">
          <strong>{stageLabel[run.stage]}</strong>
          <small>
            {run.progress}% · {counter}
          </small>
        </span>
        <span className="sr-only" aria-live="polite">
          {statusLabel[run.status]}. {stageLabel[run.stage]}.
        </span>
      </div>
      <progress
        max="100"
        value={run.progress}
        aria-label={`${stageLabel[run.stage]}: ${run.progress}%, ${counter}`}
      />
      <div className="ota-run-telemetry__health">
        <strong>{healthLabel}</strong>
        <small>
          Actualizado {clockTime(run.heartbeatAt ?? run.progressUpdatedAt ?? undefined)}
        </small>
      </div>
      <button
        type="button"
        className="ota-run-cancel"
        disabled={cancelling || run.status === "CANCELLING"}
        onClick={onCancel}
      >
        {run.status === "CANCELLING" ? "Cancelando…" : "Cancelar"}
      </button>
    </div>
  );
}

function CancelSyncModal({
  run,
  pending,
  error,
  onClose,
  onConfirm,
}: {
  run: ReservationSyncRun;
  pending: boolean;
  error: Error | null;
  onClose(): void;
  onConfirm(): void;
}) {
  const closeButton = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLElement>(null);
  const titleId = `ota-cancel-sync-title-${run.id}`;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => closeButton.current?.focus());
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !pending) {
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialog.current?.querySelectorAll<HTMLElement>(
          "button:not(:disabled)",
        ) ?? [],
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", keydown);
    };
  }, [pending]);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !pending) onClose();
      }}
    >
      <section
        ref={dialog}
        className="import-modal ota-cancel-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header>
          <div>
            <small>Venice · {run.queryFrom} → {run.queryTo}</small>
            <h2 id={titleId}>Cancelar sincronización</h2>
          </div>
          <button
            ref={closeButton}
            type="button"
            className="modal-close"
            aria-label="Cerrar modal"
            disabled={pending}
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="ota-cancel-modal__body">
          <p>
            El proceso se detendrá en el próximo punto seguro. La ejecución
            quedará en el historial y podrás reintentar el mismo rango.
          </p>
          {error && (
            <div className="message message--error" role="alert">
              {error.message}
            </div>
          )}
          <div className="ota-sync-range-actions">
            <button
              type="button"
              className="button"
              disabled={pending}
              onClick={onClose}
            >
              Continuar sincronizando
            </button>
            <button
              type="button"
              className="button button--danger"
              disabled={pending}
              onClick={onConfirm}
            >
              {pending ? "Solicitando…" : "Cancelar sincronización"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function SyncRangeModal({
  range,
  pending,
  error,
  onClose,
  onSubmit,
}: {
  range: { from: string; to: string };
  pending: boolean;
  error: Error | null;
  onClose(): void;
  onSubmit(range: { from: string; to: string }): void;
}) {
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const closeButton = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLElement>(null);
  const titleId = "ota-sync-range-title";
  const validation = rangeError(from, to);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => closeButton.current?.focus());
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !pending) {
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialog.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled)',
        ) ?? [],
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", keydown);
    };
  }, [pending]);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !pending) onClose();
      }}
    >
      <section
        ref={dialog}
        className="import-modal ota-sync-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header>
          <div>
            <small>Venice · Reservas Checkout y Canceladas</small>
            <h2 id={titleId}>Nueva sincronización</h2>
          </div>
          <button
            ref={closeButton}
            type="button"
            className="modal-close"
            aria-label="Cerrar modal"
            disabled={pending}
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <form
          className="stack-form ota-sync-range-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!validation) onSubmit({ from, to });
          }}
        >
          <p>
            Venice consultará las reservas con checkout o canceladas dentro de este rango.
            Las reservas ya existentes se actualizarán sin duplicarse.
            Las agencias inactivas se guardarán sólo como referencia y no
            ingresarán a la gestión de cobranzas.
          </p>
          <div className="ota-sync-range-fields">
            <label>
              <span>Desde</span>
              <input
                aria-label="Sincronizar desde"
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </label>
            <label>
              <span>Hasta</span>
              <input
                aria-label="Sincronizar hasta"
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </label>
          </div>
          {validation && (
            <div className="message message--warning" role="alert">
              {validation}
            </div>
          )}
          {error && (
            <div className="message message--error" role="alert">
              {error.message}
            </div>
          )}
          <div className="ota-sync-range-actions">
            <button
              type="button"
              className="button"
              disabled={pending}
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="button button--primary"
              disabled={pending || Boolean(validation)}
            >
              <VeniceSyncIcon active={pending} />
              {pending ? "Sincronizando…" : "Sincronizar reservas"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}


export function OtaPage({ view = "data" }: { view?: View }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { hotelId, period } = useWorkspace();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") ?? 1);
  const requestedPageSize = Number(params.get("pageSize") ?? 25);
  const pageSize = OTA_PAGE_SIZE_OPTIONS.some(
    (option) => option === requestedPageSize,
  ) ? requestedPageSize : 25;
  const status = otaStatusFilter(params.get("status") ?? "all");
  const reservationStatus = otaReservationStatusFilter(params.get("reservationStatus") ?? "all");
  const q = params.get("q") ?? "";
  const masterId = params.get("masterId") ?? "";
  const from = params.get("from") ?? `${period}-01`;
  const to = params.get("to") ?? monthEnd(period);
  const invalidRange = rangeError(from, to);
  const [search, setSearch] = useState(q);
  const [selectedCollections, setSelectedCollections] = useState<Set<string>>(
    () => new Set(),
  );
  const [bulkStatus, setBulkStatus] =
    useState<OtaCollectionStatus>("PENDING");
  const [bulkNotes, setBulkNotes] = useState("");
  const [durationNow, setDurationNow] = useState(() => Date.now());
  const selectPageRef = useRef<HTMLInputElement>(null);
  const reservationsTableRef = useRef<HTMLTableElement>(null);
  const [reservationHeaderScrollLeft, setReservationHeaderScrollLeft] = useState(0);
  const [reservationColumnLayout, setReservationColumnLayout] = useState({
    width: 0,
    columns: [] as number[],
  });
  const [syncRange, setSyncRange] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [cancelTarget, setCancelTarget] =
    useState<ReservationSyncRun | null>(null);
  const syncReturnFocus = useRef<HTMLElement | null>(null);
  const cancelReturnFocus = useRef<HTMLElement | null>(null);
  const settledRunRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      (view !== "data" && view !== "syncs") ||
      (params.has("from") && params.has("to"))
    ) return;
    const next = new URLSearchParams(params);
    if (!next.has("from")) next.set("from", `${period}-01`);
    if (!next.has("to")) next.set("to", monthEnd(period));
    setParams(next, { replace: true });
  }, [params, period, setParams, view]);

  const latest = useQuery({
    queryKey: ["ota-sync-latest", hotelId],
    queryFn: async (): Promise<ReservationSyncRun | null> => {
      const run = await responseData(
        apiClient.GET("/api/v1/ota/reservation-syncs/latest", {
          params: { query: { hotelId } },
        }),
      );
      return run ?? null;
    },
    enabled: Boolean(hotelId),
    refetchInterval: (query) =>
      isActiveRun(query.state.data)
        ? 1000
        : false,
    refetchIntervalInBackground: false,
  });
  const reservations = useQuery({
    queryKey: ["ota-reservations", hotelId, from, to, page, pageSize, status, reservationStatus, q, masterId],
    queryFn: (): Promise<PageResponse<OtaReservation>> => responseData(apiClient.GET("/api/v1/ota/reservations", {
      params: { query: { hotelId, from, to, page: String(page), pageSize: String(pageSize), status, reservationStatus, q, masterId } },
    })),
    enabled: view === "data" && Boolean(hotelId) && !invalidRange,
  });
  const agencyOptions = useQuery({
    queryKey: ["reconciliation-master-options", "OTA"],
    queryFn: async (): Promise<ReconciliationMaster[]> => {
      const result = await responseData(apiClient.GET("/api/v1/ota/agencies", { params: { query: { pageSize: 100, active: "true" } } }));
      return result.items.map((item): ReconciliationMaster => ({ ...item, domain: "OTA", sortOrder: 0, reportProfile: item.reportProfile ?? "" }));
    },
    enabled: view === "data",
  });
  const visibleCollectionKeys = [
    ...new Set(reservations.data?.items.map((row) => row.collectionKey) ?? []),
  ];
  const selectedVisibleCount = visibleCollectionKeys.filter((key) =>
    selectedCollections.has(key),
  ).length;
  const allVisibleSelected =
    visibleCollectionKeys.length > 0 &&
    selectedVisibleCount === visibleCollectionKeys.length;
  const toggleVisibleSelections = () =>
    setSelectedCollections((current) => {
      const next = new Set(current);
      visibleCollectionKeys.forEach((key) => {
        if (allVisibleSelected) next.delete(key);
        else next.add(key);
      });
      return next;
    });

  useEffect(() => {
    setSelectedCollections(new Set());
    setBulkNotes("");
  }, [hotelId, from, to, status, q, masterId]);

  useEffect(() => {
    if (selectPageRef.current)
      selectPageRef.current.indeterminate =
        selectedVisibleCount > 0 && !allVisibleSelected;
  }, [allVisibleSelected, selectedVisibleCount]);

  useEffect(() => {
    if (view !== "data") return;
    const table = reservationsTableRef.current;
    if (!table) return;
    const updateLayout = () => {
      const cells = table.querySelectorAll<HTMLTableCellElement>("tbody tr:first-child > td");
      if (!cells.length) return;
      const columns = [...cells].map((cell) => Math.round(cell.getBoundingClientRect().width));
      const width = Math.round(table.getBoundingClientRect().width);
      if (!width || columns.some((value) => !value)) return;
      setReservationColumnLayout((current) =>
        current.width === width && current.columns.join(",") === columns.join(",")
          ? current
          : { width, columns },
      );
    };
    const frame = requestAnimationFrame(updateLayout);
    const observer = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(updateLayout);
    observer?.observe(table);
    window.addEventListener("resize", updateLayout);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", updateLayout);
    };
  }, [view, reservations.data?.items]);
  const runs = useQuery({
    queryKey: ["ota-sync-runs", hotelId, from, to, page],
    queryFn: (): Promise<PageResponse<ReservationSyncRun>> => responseData(apiClient.GET("/api/v1/ota/reservation-syncs", {
      params: { query: { hotelId, from, to, page: String(page), pageSize: "20" } },
    })),
    enabled:
      view === "syncs" && Boolean(hotelId) && !invalidRange,
    refetchInterval: (query) =>
      query.state.data?.items.some((run) => isActiveRun(run)) ? 1000 : false,
    refetchIntervalInBackground: false,
  });
  const hasActiveHistoryRun =
    view === "syncs" &&
    Boolean(runs.data?.items.some((run) => isActiveRun(run)));
  useEffect(() => {
    if (!hasActiveHistoryRun) return;
    setDurationNow(Date.now());
    const timer = window.setInterval(() => setDurationNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [hasActiveHistoryRun]);
  const activeRun =
    (isActiveRun(latest.data)
      ? latest.data
      : view === "syncs"
        ? runs.data?.items.find((run) => isActiveRun(run))
        : null) ?? null;
  const observedRun =
    view === "syncs" ? runs.data?.items[0] ?? null : latest.data ?? null;

  useEffect(() => {
    if (!observedRun || isActiveRun(observedRun)) {
      if (observedRun) settledRunRef.current = null;
      return;
    }
    if (settledRunRef.current === observedRun.id) return;
    settledRunRef.current = observedRun.id;
    void Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["ota-sync-runs", hotelId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["ota-reservations", hotelId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["reconciliation-master-options", "OTA"],
      }),
    ]);
  }, [hotelId, observedRun?.id, observedRun?.status, queryClient]);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["ota-sync-latest", hotelId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["ota-sync-runs", hotelId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["ota-reservations", hotelId],
      }),
    ]);
  };
  const sync = useMutation({
    mutationFn: (range: { from: string; to: string }) => responseData(apiClient.POST("/api/v1/ota/reservations/sync", {
      body: { hotelId, ...range },
    })),
    onSuccess: async () => {
      toast.success("Sincronización iniciada.");
      setSyncRange(null);
      requestAnimationFrame(() => syncReturnFocus.current?.focus());
      await refresh();
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo iniciar la sincronización." }),
  });
  const cancelSync = useMutation({
    mutationFn: (runId: string): Promise<ReservationSyncRun> => responseData(apiClient.POST("/api/v1/ota/reservation-syncs/{id}/cancel", {
      params: { path: { id: runId } },
    })),
    onSuccess: async () => {
      toast.success("Cancelación solicitada.");
      setCancelTarget(null);
      requestAnimationFrame(() => cancelReturnFocus.current?.focus());
      await refresh();
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo cancelar la sincronización." }),
  });
  const saveCollection = useMutation({
    mutationFn: ({
      row,
      values,
    }: {
      row: OtaReservation;
      values: { status: OtaCollectionStatus; notes?: string };
    }) => responseData(apiClient.PATCH("/api/v1/ota/reservation-collections", {
        body: {
          hotelId,
          collectionKeys: [row.collectionKey],
          ...values,
        },
      })),
    onSuccess: async () => {
      toast.success("Se actualizó la cobranza de la reserva.");
      await queryClient.invalidateQueries({
        queryKey: ["ota-reservations", hotelId],
      });
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo actualizar la cobranza." }),
  });
  const saveManyCollections = useMutation({
    mutationFn: () => {
      const notes = bulkNotes.trim();
      return responseData(apiClient.PATCH("/api/v1/ota/reservation-collections", {
        body: {
          hotelId,
          collectionKeys: [...selectedCollections],
          status: bulkStatus,
          ...(notes ? { notes } : {}),
        },
      }));
    },
    onSuccess: async ({ affected }) => {
      toast.success(
        `Se actualizaron ${affected} ${affected === 1 ? "reserva" : "reservas"}.`,
      );
      setSelectedCollections(new Set());
      setBulkNotes("");
      await queryClient.invalidateQueries({
        queryKey: ["ota-reservations", hotelId],
      });
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudieron actualizar las reservas." }),
  });
  const setPage = (value: number) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.set("page", String(value));
      return next;
    });
  };
  const setPageSize = (value: number) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.set("pageSize", String(value));
      next.set("page", "1");
      return next;
    });
  };
  const setFilters = (values: Record<string, string>) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      Object.entries(values).forEach(([key, value]) =>
        value ? next.set(key, value) : next.delete(key),
      );
      next.set("page", "1");
      return next;
    });
  };
  const setRangeField = (field: "from" | "to", value: string) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.set(field, value);
      next.set("page", "1");
      return next;
    });
  };
  const openSync = (range?: { from: string; to: string }) => {
    const active =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    syncReturnFocus.current =
      active
        ?.closest(".domain-context-card__menu")
        ?.querySelector<HTMLElement>(".context-kebab") ??
      active;
    setSyncRange(range ?? lastCompleteMonthRange());
  };
  const openCancel = (run: ReservationSyncRun) => {
    const active =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    cancelReturnFocus.current =
      active
        ?.closest(".domain-context-card__menu")
        ?.querySelector<HTMLElement>(".context-kebab") ??
      active;
    cancelSync.reset();
    setCancelTarget(run);
  };
  const error =
    latest.error ??
    reservations.error ??
    runs.error ??
    agencyOptions.error;
  const syncActive = sync.isPending || Boolean(activeRun);

  return (
    <>
      <DomainContext
        domain="OTA"
        moduleName="Cobranzas OTA"
        compactFilters
        compactContext
        actions={
          <>
            <a
              className={`context-action${invalidRange ? " context-action--disabled" : ""}`}
              href={
                invalidRange
                  ? undefined
                  : `/api/v1/ota/reports/reservations.xlsx?${new URLSearchParams({ hotelId, from, to })}`
              }
              aria-disabled={Boolean(invalidRange)}
            >
              <DownloadIcon />
              <span>Exportar reporte</span>
            </a>
            <Link
              className="context-action"
              to={{
                pathname: "/cobranzas-ota/sincronizaciones",
                search: new URLSearchParams({ from, to }).toString(),
              }}
            >
              <VeniceSyncIcon />
              <span>Sincronizaciones</span>
            </Link>
          </>
        }
        dateRange={
          view === "data" || view === "syncs"
            ? { from, to, onChange: setRangeField }
            : undefined
        }
        hideMaster
      >
        <PageLead
          eyebrow="Reservas cerradas y cobranzas"
          title="Cobranzas OTA"
          description="Reservas sincronizadas desde Sistema de Gestión Hotelera."
        />
      </DomainContext>

      {error && (
        <div className="message message--error">
          {error instanceof Error
            ? error.message
            : "No se pudo completar la operación."}
        </div>
      )}
      {view === "data" && (
        <Panel
          title="Historial de reservas"
          meta={`${reservations.data?.meta.total ?? 0} resultados`}
          className="ota-reservations-panel"
          stickyHeader
          headerContent={
            <>
              {invalidRange && (
                <div className="message message--warning" role="alert">
                  {invalidRange}
                </div>
              )}
              <form
                className="filter-bar ota-reservation-filters"
                onSubmit={(event) => {
                  event.preventDefault();
                  setFilters({ q: search });
                }}
              >
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Agencia, reserva, localizador, huésped u observación"
                  aria-label="Buscar reservas"
                />
                <label className="ota-reservation-filter-field">
                  <span>Filtrar por OTA / Agencia</span>
                  <select
                    aria-label="Filtrar reservas por OTA / Agencia"
                    value={masterId}
                    onChange={(event) => setFilters({ masterId: event.target.value })}
                  >
                    <option value="">Todas</option>
                    {agencyOptions.data?.map((agency) => (
                      <option key={agency.id} value={agency.id}>{agency.name}</option>
                    ))}
                  </select>
                </label>
                <label className="ota-reservation-filter-field">
                  <span>Estado de cobranza</span>
                  <select
                    aria-label="Estado de cobranza"
                    value={status}
                    onChange={(event) => setFilters({ status: event.target.value })}
                  >
                    <option value="all">Todos</option>
                    <option value="pending">Pendientes</option>
                    <option value="claimed">Reclamadas</option>
                    <option value="prepurchase">Precompra</option>
                    <option value="collected">Cobradas</option>
                  </select>
                </label>
                <label className="ota-reservation-filter-field">
                  <span>Estado de reserva</span>
                  <select
                    aria-label="Estado de reserva"
                    value={reservationStatus}
                    onChange={(event) => setFilters({ reservationStatus: event.target.value })}
                  >
                    <option value="all">Todas</option>
                    <option value="confirmed">Confirmada</option>
                    <option value="checked_out">Checkout</option>
                    <option value="cancelled">Cancelada</option>
                  </select>
                </label>
                <button className="button button--small">Buscar</button>
              </form>
              {selectedCollections.size > 0 && (
                <section className="ota-bulk-actions" aria-label="Acciones masivas de cobranza">
                  <strong>
                    {selectedCollections.size} {selectedCollections.size === 1 ? "reserva seleccionada" : "reservas seleccionadas"}
                  </strong>
                  <label>
                    <span>Nuevo estado</span>
                    <select value={bulkStatus} onChange={(event) => setBulkStatus(event.target.value as OtaCollectionStatus)}>
                      {Object.entries(collectionStatusLabel).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="ota-bulk-actions__notes">
                    <span>Observación opcional</span>
                    <textarea
                      value={bulkNotes}
                      onChange={(event) => setBulkNotes(event.target.value)}
                      rows={1}
                      placeholder="Si queda vacía, conserva las observaciones actuales."
                    />
                  </label>
                  <div className="ota-bulk-actions__buttons">
                    <button
                      type="button"
                      className="button button--small"
                      disabled={saveManyCollections.isPending}
                      onClick={() => {
                        setSelectedCollections(new Set());
                        setBulkNotes("");
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="button button--primary button--small"
                      disabled={saveManyCollections.isPending}
                      onClick={() => saveManyCollections.mutate()}
                    >
                      {saveManyCollections.isPending ? "Aplicando…" : "Guardar"}
                    </button>
                  </div>
                </section>
              )}
              <ReservationColumnHeader
                allVisibleSelected={allVisibleSelected}
                hasVisibleReservations={Boolean(visibleCollectionKeys.length)}
                onTogglePage={toggleVisibleSelections}
                selectPageRef={selectPageRef}
                scrollLeft={reservationHeaderScrollLeft}
                layout={reservationColumnLayout}
              />
            </>
          }
        >
          <div
            className="table-wrap ota-reservations-table"
            onScroll={(event) => setReservationHeaderScrollLeft(event.currentTarget.scrollLeft)}
          >
            <table ref={reservationsTableRef}>
              <thead className="sr-only">
                <tr>
                  <th>Seleccionar</th>
                  <th>Agencia / origen</th>
                  <th>Reserva</th>
                  <th>Estado</th>
                  <th>Huésped</th>
                  <th>Estadía</th>
                  <th>Importe</th>
                  <th>Cobranza</th>
                  <th>Observaciones</th>
                </tr>
              </thead>
              <tbody>
                {reservations.data?.items.map((row) => (
                  <tr
                    key={row.id}
                    className={selectedCollections.has(row.collectionKey) ? "is-selected" : undefined}
                  >
                    <td className="ota-selection-cell">
                      <label className="check-field">
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar reserva ${row.reservationId}`}
                          checked={selectedCollections.has(row.collectionKey)}
                          onChange={() =>
                            setSelectedCollections((current) => {
                              const next = new Set(current);
                              if (next.has(row.collectionKey)) next.delete(row.collectionKey);
                              else next.add(row.collectionKey);
                              return next;
                            })
                          }
                        />
                      </label>
                    </td>
                    <td>
                      <strong>{row.agency}</strong>
                      {row.sourceAgency && <small>{row.sourceAgency}</small>}
                      <small className="ota-source">
                        Venice · {dateTime(row.lastSyncedAt)}
                      </small>
                    </td>
                    <td>
                      <strong className="mono">{row.reservationId}</strong>
                      <small className="mono">
                        {row.locator || "Sin localizador"}
                      </small>
                    </td>
                    <td>
                      <span className={`status status--${reservationStatusTone[row.status]}`}>
                        {reservationStatusLabel[row.status]}
                      </span>
                    </td>
                    <td>{row.guestName || "—"}</td>
                    <td>
                      {shortDate(row.arrivalDate)} →{" "}
                      {shortDate(row.departureDate)}
                    </td>
                    <td className="mono">
                      <strong>{money(row.netTotalAmount, row.currency)}</strong>
                      <small>
                        Bruto {money(row.grossTotalAmount, row.currency)}
                      </small>
                    </td>
                    <td>
                      <CollectionStatusEditor
                        row={row}
                        saving={saveCollection.isPending}
                        onSave={(status) =>
                          saveCollection.mutate({ row, values: { status } })
                        }
                      />
                    </td>
                    <td>
                      <CollectionNotesEditor
                        row={row}
                        saving={saveCollection.isPending}
                        onSave={(notes) =>
                          saveCollection.mutate({
                            row,
                            values: { status: row.collectionStatus, notes },
                          })
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!reservations.data?.items.length && (
            <Empty>
              <span>
                {latest.data?.status === "COMPLETED"
                  ? "No hay reservas Checkout o Canceladas para estos filtros."
                  : "Sincronizá las reservas del período."}
              </span>
              <button
                type="button"
                className="button button--primary button--small"
                disabled={syncActive}
                onClick={() => openSync({ from, to })}
              >
                {activeRun
                  ? activeRun.status === "PENDING"
                    ? `En cola · ${activeRun.progress}%`
                    : activeRun.status === "CANCELLING"
                      ? `Cancelando · ${activeRun.progress}%`
                      : `Sincronizando · ${activeRun.progress}%`
                  : sync.isPending
                    ? "Iniciando…"
                    : "Nueva sincronización"}
              </button>
            </Empty>
          )}
          <Pagination
            meta={reservations.data?.meta}
            pageSizeOptions={OTA_PAGE_SIZE_OPTIONS}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </Panel>
      )}

      {view === "syncs" && (
        <Panel
          meta={`${runs.data?.meta.total ?? 0} ejecuciones`}
          headerStart={
            <Link
              className="back-link"
              to={{
                pathname: "/cobranzas-ota/reservas",
                search: new URLSearchParams({ from, to }).toString(),
              }}
            >
              ← Volver a reservas
            </Link>
          }
        >
          {invalidRange && (
            <div className="message message--warning" role="alert">
              {invalidRange}
            </div>
          )}
          <div className="ota-run-toolbar">
            <h2>Sincronizaciones</h2>
            <button
              type="button"
              className="button button--primary button--small"
              disabled={syncActive}
              onClick={() => openSync()}
            >
              <VeniceSyncIcon active={syncActive} />
              {activeRun
                ? activeRun.status === "PENDING"
                  ? `En cola · ${activeRun.progress}%`
                  : activeRun.status === "CANCELLING"
                    ? `Cancelando · ${activeRun.progress}%`
                    : `Sincronizando · ${activeRun.progress}%`
                : sync.isPending
                  ? "Iniciando…"
                  : "Nueva sincronización"}
            </button>
          </div>
          <p className="ota-run-description">
            Cada ejecución conserva el rango consultado y sus incidencias.
          </p>
          <div className="ota-run-list">
            {runs.data?.items.map((run) => (
              <article key={run.id}>
                <div
                  className={`ota-run-summary${isActiveRun(run) ? " ota-run-summary--active" : ""}`}
                >
                  <span className={`status status--${statusTone(run.status)}`}>
                    {statusLabel[run.status]}
                  </span>
                  <SyncRunIssues run={run} />
                  <div className="ota-run-summary__date">
                    <strong>{dateTime(run.createdAt)}</strong>
                    <small>
                      {run.queryFrom} → {run.queryTo}
                    </small>
                    {(run.status === "FAILED" ||
                      run.status === "CANCELLED") && (
                      <button
                        type="button"
                        className="ota-run-retry"
                        disabled={syncActive}
                        onClick={() =>
                          openSync({
                            from: run.queryFrom,
                            to: run.queryTo,
                          })
                        }
                      >
                        Reintentar este rango
                      </button>
                    )}
                  </div>
                  <dl>
                    <div>
                      <dt>Leídas</dt>
                      <dd>{run.fetchedCount}</dd>
                    </div>
                    <div>
                      <dt>Guardadas</dt>
                      <dd>{run.upsertedCount}</dd>
                    </div>
                    <div>
                      <dt>Para cobranzas</dt>
                      <dd>{run.eligibleCount}</dd>
                    </div>
                    <div>
                      <dt>Tiempo total</dt>
                      <dd>{syncDuration(run, durationNow)}</dd>
                    </div>
                  </dl>
                  {isActiveRun(run) && (
                    <SyncTelemetry
                      run={run}
                      cancelling={cancelSync.isPending}
                      onCancel={() => openCancel(run)}
                    />
                  )}
                </div>
              </article>
            ))}
          </div>
          {!invalidRange && !runs.data?.items.length && (
            <Empty>
              No hay sincronizaciones para el rango seleccionado.
            </Empty>
          )}
          {!invalidRange && (
            <Pagination meta={runs.data?.meta} onPageChange={setPage} />
          )}
        </Panel>
      )}

      {syncRange && (
        <SyncRangeModal
          range={syncRange}
          pending={sync.isPending}
          error={null}
          onClose={() => {
            if (!sync.isPending) {
              sync.reset();
              setSyncRange(null);
              requestAnimationFrame(() => syncReturnFocus.current?.focus());
            }
          }}
          onSubmit={(range) => sync.mutate(range)}
        />
      )}
      {cancelTarget && (
        <CancelSyncModal
          run={cancelTarget}
          pending={cancelSync.isPending}
          error={null}
          onClose={() => {
            if (!cancelSync.isPending) {
              cancelSync.reset();
              setCancelTarget(null);
              requestAnimationFrame(() => cancelReturnFocus.current?.focus());
            }
          }}
          onConfirm={() => cancelSync.mutate(cancelTarget.id)}
        />
      )}
    </>
  );
}
